import { Injectable } from '@angular/core';
import { CommonDataParamsService } from './common-data-params.service';
import {
  ScoreTimeSeriesRequestBody,
  ScoreTimeSeriesEventFilter,
  ScoreTimeSeriesTimeRange,
  ScoreRequestTypes,
  ScoreType,
  CustomScore,
} from '../models/score-time-series.interface';
import getScoreTypeDetails from './score-type-details';
import { AuthService } from 'src/app/service/auth.service';

/**
 * Parameters for building score time series request
 * NOTE: Only 'entities' type is supported (no search_query)
 *
 * @example Standalone usage (explicit values)
 * {
 *   score_type: 'esg',
 *   entity: ['bmw'],
 *   entityType: 'company',
 *   time: '2025-07-24--2025-10-24'
 * }
 *
 * @example Query params usage (falls back to URL params)
 * {
 *   score_type: 'esg'
 *   // entity, entityType, time will be resolved from query params
 * }
 *
 * @example Mixed usage (override some, use query params for others)
 * {
 *   score_type: 'esg',
 *   time: '30'  // override time, use query params for entity
 * }
 */
export interface ScoreTimeSeriesContentParams {
  score_type: ScoreRequestTypes;
  collapse_on?: 'participant'[];
  entity?: string[];
  entityType?: string;
  time?: string;
  timePeriod?: string;
  newsday?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreUtilsService {
  constructor(
    private commonDataParams: CommonDataParamsService,
    private auth: AuthService,
  ) {}

  // ============================================================================
  // Custom Score Resolution Methods
  // ============================================================================

  /**
   * Maps a user-facing score ID to the canonical API score key
   * (`credit_risk` → `credit_watch`, etc.). Falls back to the input id
   * for unrecognized values (e.g. custom score uids), matching how
   * `getScoreTypeDetails` behaves at the call sites.
   */
  static resolveScoreKey(scoreId: string): string {
    return getScoreTypeDetails(scoreId).scoreKey || scoreId;
  }

  /**
   * Checks if a score ID is a predefined score type (not custom).
   * Uses getScoreTypeDetails mapping as the single source of truth.
   */
  isPredefinedScore(scoreId: string): boolean {
    const details = getScoreTypeDetails(scoreId);
    return !!details.scoreKey;
  }

  /**
   * Resolves any score ID to API-compatible format.
   * - Predefined scores: returns as ScoreType string
   * - Custom scores: transforms to CustomScore object with event_weights
   */
  resolveScoreType(scoreId: string): ScoreRequestTypes {
    if (this.isPredefinedScore(scoreId)) {
      return scoreId as ScoreType;
    }
    return this.transformCustomScore(scoreId);
  }

  /**
   * Transforms a user's custom score (from auth.scorelabSettings.customScores)
   * to the API CustomScore format.
   *
   * User format: { weight: 5, polarity: -1, weight_persistence: 90 }
   */
  private transformCustomScore(scoreUid: string): CustomScore | ScoreType {
    const customScores = this.auth.scorelabSettings.customScores;
    const customScore = customScores?.find(
      (s: { uid: string }) => s.uid === scoreUid,
    );

    if (!customScore?.event_weights?.length) {
      // Fallback to general_risk if custom score not found or has no weights
      return 'general_risk';
    }

    return {
      event_weights: customScore.event_weights.map(
        (ew: {
          event_id: string;
          weight: number;
          polarity: number;
          weight_persistence?: number;
        }) => ({
          event_id: ew.event_id,
          weight: ew.weight * ew.polarity, // API expects signed weight
        }),
      ),
    };
  }

  // ============================================================================
  // Score Time Series Utilities
  // ============================================================================

  /**
   * Returns the last non-null score from a time series array.
   */
  static getLastScore(
    timeSeries: Array<{ score: number | null }>,
  ): number | null {
    for (let i = timeSeries.length - 1; i >= 0; i--) {
      if (timeSeries[i].score !== null && timeSeries[i].score !== undefined) {
        return timeSeries[i].score;
      }
    }
    return null;
  }

  /**
   * Calculates bull/bear trend from a score value.
   * Centralized so all consumers (news, scanner, etc.) use the same logic.
   */
  static getBullBearTrend(score: number | null): {
    trendType: 'bull' | 'bear' | null;
    trendValue: string | null;
  } {
    if (score === null) {
      return { trendType: null, trendValue: null };
    }
    return {
      trendType: score > 5.0 ? 'bull' : 'bear',
      trendValue: score.toFixed(1),
    };
  }

  // ============================================================================
  // Request Building Methods
  // ============================================================================

  private resolveTimeRange(
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesTimeRange {
    return this.commonDataParams.generateTimeRange({
      type: 'entities',
      time: params.time,
      timePeriod: params.timePeriod,
      newsday: params.newsday,
    });
  }

  private createEventFilter(
    eventParticipants: string[],
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesEventFilter {
    const eventFilter: ScoreTimeSeriesEventFilter = {};

    if (eventParticipants.length > 0) {
      eventFilter.event_participants = eventParticipants;
    }

    if (!params.entity) {
      const queryParams = this.commonDataParams.getCurrentQueryParams();

      const eventIds = this.commonDataParams.getEventIds(queryParams);
      if (eventIds) {
        eventFilter.event_ids = eventIds;
      }

      if (queryParams.temporality) {
        eventFilter.temporality = queryParams.temporality.split(',');
      }

      if (queryParams.factuality) {
        eventFilter.factuality = this.commonDataParams.getFactualityValues(
          queryParams.factuality,
        );
      }
    }

    return eventFilter;
  }

  private resolveApiScoreType(scoreType: ScoreRequestTypes): ScoreRequestTypes {
    if (typeof scoreType === 'object') {
      return scoreType;
    }

    const details = getScoreTypeDetails(scoreType);
    return (details.scoreKey || scoreType) as ScoreRequestTypes;
  }

  /**
   *
   * @param apiOverrides - Optional API fields to override (e.g., collapse_on)
   * @param params - Content params for building the request
   */
  createScoreTimeSeriesRequest(
    apiOverrides: Partial<ScoreTimeSeriesRequestBody>,
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesRequestBody {
    const eventParticipants = this.commonDataParams.getEventParticipants({
      entity: params.entity,
      entityType: params.entityType,
    });
    const eventFilter = this.createEventFilter(eventParticipants, params);

    const apiScoreType = this.resolveApiScoreType(params.score_type);

    const requestBody: ScoreTimeSeriesRequestBody = {
      score_type: apiScoreType,
      time_range: this.resolveTimeRange(params),
    };

    if (Object.keys(eventFilter).length > 0) {
      requestBody.event_filter = eventFilter;
    }

    if (params.collapse_on) {
      requestBody.collapse_on = params.collapse_on;
    }

    return {
      ...requestBody,
      ...apiOverrides,
    };
  }
}
